import ThemedSelect from "../shared/ThemedSelect.jsx";
import { useEffect, useRef, useState } from "react";
import { Alert, Badge, Button, Form, Modal, Table } from "react-bootstrap";
import InvoiceDocument from "./InvoiceDocument.jsx";
import InvoiceView from "./InvoiceView.jsx";
import InvoiceSidePanel, { invoiceToPreview } from "./InvoiceSidePanel.jsx";
import InvoicePhotoPicker from "./InvoicePhotoPicker.jsx";
import TabHeader from "./TabHeader.jsx";
import {
  listInvoices,
  getInvoice,
  createInvoiceFromProgressClaim,
  createInvoiceFromQuote,
  replaceQuotedInvoice,
  createExcessInvoice,
  createManualInvoice,
  getInvoiceCreationState,
  updateInvoice,
  markInvoiceSent,
  reopenInvoice,
  getInvoiceEvidence,
  listInvoicePhotos,
  voidInvoice,
  listProgressClaims,
  getClaim,
  updateClaim,
  listExpectedInvoiceDates,
  setExpectedInvoiceDates,
  replaceExpectedInvoiceDates,
  clearExpectedInvoiceDates,
  listEntityRows,
} from "../../services/apiClient.js";
import { formatDate } from "../../utils/date.js";

// Invoices tab — persisted tax invoices for the claim. Sources: a progress-claim
// cycle, the quote itself (stamps its lines so nothing bills twice; Final Quote jobs
// get a Final Quoted Invoice), the excess (billed to the client, one per claim),
// Do & Charge (from the Orders tab), and manual Other / Quote Fee invoices.
// Which creators are offered follows legacy EnableAppropriateControls (see
// docs/business-rules/invoices.md).

const nzd = new Intl.NumberFormat("en-NZ", { style: "currency", currency: "NZD" });
const money = (v) => nzd.format(Number(v || 0));
// Invoice types whose "All details" switch starts OFF regardless of the insurer.
// Named as a set rather than tested inline so the list is one edit when Karen
// refines it, which she has said she will.
const OPENS_SUMMARISED = new Set(["Quoted Invoice", "Final Quoted Invoice", "Other Invoice"]);

// "84271 76562": invoice number, then GPS number — 1.0's file name, which the
// office types into its filing. Exported so the quote print can adopt the same
// habit if asked.
export function printFileName(inv) {
  return [inv.invoice_no, inv.claim_number].filter(Boolean).join(" ");
}

// SENT is a different axis from paid/disputed, so it gets its own chip rather
// than competing for the one slot: an invoice can be sent AND paid AND disputed,
// and each of those is a separate fact about it.
function sentBadge(inv) {
  if (!inv.sent_at) return <Badge bg="light" text="dark" className="border me-1">Draft</Badge>;
  return (
    <Badge
      bg="info"
      className="me-1"
      data-hint={`Marked as sent ${formatDate(inv.sent_at)}${inv.sent_by_name ? ` by ${inv.sent_by_name}` : ""}`}
    >
      Sent · {formatDate(inv.sent_at)}
    </Badge>
  );
}

// Sending is the moment the FINAL question gets answered, so it is asked here
// rather than left to a button on the row.
//
// is_final means "this is the closing bill to the insurer for this job", and it
// is what takes a job off Non Invoiced Jobs and WIP In Progress Jobs. It is a
// decision about the JOB, made when the user knows this is the last one (Robert,
// 15 Aug: "what users do when they know its the last invoice and closing out"),
// which is exactly when they are sending it. It also has to be answered by then:
// is_final is a DOCUMENT field, so after sending it takes a reopen to change.
//
// Defaulted OFF on purpose, even though 1.0's users tick it on ~86% of invoices.
// The two failure modes are not equal. A job left unmarked stays on the chase
// list, gets looked at, gets cleared. A job marked by accident drops OFF the
// chase list and is never chased again, which is an unbilled job found by
// nobody. Default off makes the expensive mistake the visible one.
function SendInvoiceModal({ state, otherFinal, busy, onChange, onConfirm, onClose }) {
  if (!state) return null;
  const { inv, final } = state;
  return (
    <Modal show onHide={onClose} centered>
      <Modal.Header closeButton>
        <Modal.Title>Mark invoice #{inv.invoice_no} as sent?</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <p className="mb-3">
          Its lines, amounts, dates and photographs are locked from then on.
          Payment and dispute details stay editable.
        </p>
        <Form.Check
          type="checkbox"
          id="send-is-final"
          checked={final}
          disabled={busy}
          onChange={(e) => onChange(e.target.checked)}
          label={<strong>This is the final invoice for this job</strong>}
        />
        <div className="text-muted small ms-4 mt-1">
          Ticking this closes the job&rsquo;s billing: it comes off
          {" "}<em>Non Invoiced Jobs</em> and <em>WIP In Progress Jobs</em>. Leave it
          clear if more is still to be billed.
        </div>
        {final && otherFinal.length ? (
          <Alert variant="warning" className="mt-3 mb-0 py-2 small">
            Invoice{otherFinal.length > 1 ? "s" : ""}{" "}
            {otherFinal.map((o) => `#${o.invoice_no}`).join(", ")} on this job
            {otherFinal.length > 1 ? " are" : " is"} already marked as final.
            {otherFinal.some((o) => o.sent_at)
              ? " Already sent, so that marker stays until the invoice is reopened."
              : " That marker will be cleared."}
          </Alert>
        ) : null}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" size="sm" onClick={onClose} disabled={busy}>Cancel</Button>
        <Button size="sm" onClick={onConfirm} disabled={busy}>
          {busy ? "Working…" : "Mark as Sent"}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}

// Mirrors COMPLETION_INVOICE_TYPES in services/invoices.js — the invoices that
// claim WORK IS DONE, and so the ones an insurer can require photographs on.
// Kept in step by hand because the server is the authority; this copy exists
// only so the UI can offer the right next step instead of provoking a refusal.
const NEEDS_EVIDENCE = new Set(["Quoted Invoice", "Final Quoted Invoice"]);

function statusBadge(inv) {
  if (inv.disputed) return <Badge bg="danger">Disputed</Badge>;
  if (inv.has_been_paid) return <Badge bg="success">Paid{inv.date_paid ? ` · ${formatDate(inv.date_paid)}` : ""}</Badge>;
  // A not-paid REASON excuses the invoice (Karen 2026-07-22, 1.0 parity:
  // "No - Waived" etc. — any reason drops it from the outstanding chase).
  if (inv.not_paid_reason) return <Badge bg="secondary">Not paid · {inv.not_paid_reason}</Badge>;
  return <Badge bg="warning" text="dark">Outstanding</Badge>;
}

function ClaimInvoicesTab({ claimId }) {
  const [invoices, setInvoices] = useState([]);
  const [cycles, setCycles] = useState([]);
  const [pickCycle, setPickCycle] = useState("");
  const [creation, setCreation] = useState(null); // what may be created (legacy control rules)
  const [otherAmount, setOtherAmount] = useState("");
  const [viewing, setViewing] = useState(null); // full invoice (with lines) in modal
  const [picking, setPicking] = useState(null);   // invoice whose photos are being chosen
  const [sending, setSending] = useState(null);   // { inv, final } while the send dialog is open
  const [evidence, setEvidence] = useState(null); // per-insurer requirements + completion notes
  const [viewPhotos, setViewPhotos] = useState([]); // photos for the invoice being viewed
  const viewingRef = useRef(null); // which invoice the open modal is for, for late replies
  const [viewDetailed, setViewDetailed] = useState(false); // "all details" = stamped quote lines
  const [claim, setClaim] = useState(null); // for the will-not-be-invoiced flag
  const [expected, setExpected] = useState({ rows: [], remaining: 0 }); // expected invoice dates
  const [expStart, setExpStart] = useState("");
  const [expPct, setExpPct] = useState("50");
  const [expEdit, setExpEdit] = useState(null); // null | draft rows being edited
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [notPaidReasons, setNotPaidReasons] = useState([]); // Excess Invoice canned reasons

  function load() {
    return Promise.all([
      listInvoices(claimId).then(setInvoices),
      listEntityRows("excess-not-paid-reasons").then(setNotPaidReasons).catch(() => setNotPaidReasons([])),
      listProgressClaims(claimId).then(setCycles).catch(() => setCycles([])),
      getInvoiceCreationState(claimId).then(setCreation).catch(() => setCreation(null)),
      getClaim(claimId).then(({ claim: c }) => setClaim(c)).catch(() => {}),
      listExpectedInvoiceDates(claimId).then(setExpected).catch(() => setExpected({ rows: [], remaining: 0 })),
    ]).catch(() => setError("Unable to load invoices."));
  }

  // Legacy InvoicesPage "Job Will Not Be Invoiced" link — the flag that keeps a
  // legitimately non-billable job off the not-yet-invoiced worklists.
  async function toggleWillNotInvoice(on) {
    setBusy(true);
    try {
      await updateClaim(claimId, { job_will_not_be_invoiced: on ? "true" : "false" });
      await load();
    } catch (err) {
      setError(err.response?.data?.error || "Unable to update the flag.");
    } finally {
      setBusy(false);
    }
  }
  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [claimId]);

  // --- Expected-dates row editor -------------------------------------------
  // Draft rows hold percentage as a PERCENT string (DB stores a fraction).
  function startExpectedEdit() {
    setExpEdit(
      expected.rows.length
        ? expected.rows.map((r) => ({
            expected_date: r.expected_date,
            percentage: String(+(Number(r.percentage) * 100).toFixed(1)),
          }))
        : [{ expected_date: "", percentage: "100" }]
    );
  }
  function patchExpectedRow(i, patch) {
    setExpEdit((rows) => rows.map((r, j) => (j === i ? { ...r, ...patch } : r)));
  }
  const expTotalPct = expEdit
    ? +expEdit.reduce((s, r) => s + (Number(r.percentage) || 0), 0).toFixed(1)
    : 0;
  const saveExpectedRows = () =>
    run(async () => {
      await replaceExpectedInvoiceDates(
        claimId,
        expEdit.map((r) => ({ expected_date: r.expected_date, percentage: Number(r.percentage) }))
      );
      setExpEdit(null);
    });

  // VOIDED invoices are on the list but are not invoices any more: they must
  // stay out of every calculation, or voiding one leaves the job still looking
  // billed. `live` is what every rule below reads; `invoices` is for rendering
  // only (Robert, 17 Aug).
  const live = invoices.filter((i) => !i.voided);

  // Cycles not already invoiced (by an active invoice sourced from them).
  const invoicedCycleIds = new Set(
    live.filter((i) => i.source === "progress_claim").map((i) => String(i.source_id))
  );
  // Complete cycles only (2026-08-27, matching the server rule): an open cycle
  // keeps changing, so invoicing one froze the stored totals while the printed
  // page kept moving. Finish the cycle, then it appears here.
  const availableCycles = cycles.filter((c) => c.complete && !invoicedCycleIds.has(String(c.id)));

  async function run(fn, confirmMsg) {
    if (confirmMsg && !window.confirm(confirmMsg)) return;
    setBusy(true);
    setError("");
    try {
      await fn();
      await load();
    } catch (err) {
      setError(err.response?.data?.error || "Unable to create invoice.");
    } finally {
      setBusy(false);
    }
  }
  const create = () =>
    pickCycle &&
    run(async () => {
      await createInvoiceFromProgressClaim(claimId, Number(pickCycle));
      setPickCycle("");
    });
  const createQuoted = () =>
    run(
      () => createInvoiceFromQuote(claimId),
      "Creating an invoice cannot be reversed. HAVE YOU CHECKED that no additional costs are still to be entered?"
    );
  const replaceQuoted = () =>
    run(
      () => replaceQuotedInvoice(claimId, creation.unpaid_quoted_id),
      "This will replace the invoice's lines and amount — the invoice number stays the same. Continue?"
    );
  const createExcess = () =>
    run(() => createExcessInvoice(claimId), "Create an Excess Invoice billed to the client?");
  const createOther = (type) =>
    run(() => createManualInvoice(claimId, { invoice_type: type, amount: otherAmount === "" ? undefined : Number(otherAmount) }).then(() => setOtherAmount("")));
  useEffect(() => {
    const ac = new AbortController();
    getInvoiceEvidence(claimId)
      .then((e) => !ac.signal.aborted && setEvidence(e))
      .catch(() => !ac.signal.aborted && setEvidence(null));
    return () => ac.abort();
  }, [claimId]);

  async function view(inv) {
    try {
      const full = await getInvoice(claimId, inv.id);
      // Quoted and Other invoices open SUMMARISED, whoever the insurer is.
      //
      // This deliberately stops matching 1.0, and that was a decision rather than
      // an oversight. 1.0's QuotedInvoice.rpt itemises (NonVeroGABQuotedInvoice.rpt
      // is the separate Vero/GAB treatment, which is why `line_detail` is still
      // decided server-side off the Vero company group), so the default used to
      // be itemised for every non-Vero insurer. Karen Sweatman asked twice for it
      // to stop — "quoted invoices are too busy, the whole quote is listed on the
      // invoice" — and it was parked on 2026-08-11 precisely because flipping it
      // changes what every non-Vero insurer receives. Robert's call, 2026-08-12,
      // "all quoted invoice and all other invoice... can refine later".
      //
      // The switch below is untouched: itemised is one click away, and
      // `line_detail` still carries 1.0's answer for anything that wants it.
      // A Do & Charge invoice OPENS ON All details (KS 2026-08-28): it bills
      // actual costs off the Orders screen, so the itemised detail IS the
      // document — a summary of it says nothing.
      setViewDetailed(
        full.source === "do_and_charge"
          ? full.detail_lines?.length > 0
          : !OPENS_SUMMARISED.has(full.invoice_type) &&
              full.line_detail === "itemised" &&
              full.detail_lines?.length > 0
      );
      // CLEAR THE PREVIOUS INVOICE'S PHOTOGRAPHS FIRST. The modal renders as soon
      // as `viewing` is set, while the photographs are still being fetched, so
      // leaving the old set in state made invoice B open showing invoice A's
      // pictures until the request came back — and Print is one click. That is
      // precisely the failure per-invoice selection exists to prevent, arriving
      // through the back door (18% of claims carry more than one invoice).
      setViewPhotos([]);
      viewingRef.current = full.id;
      setViewing(full);
      // The printed invoice carries the completion block, so the modal needs the
      // insurer's requirements and this invoice's photographs. Failures here must
      // never stop the invoice opening — the block simply does not render.
      getInvoiceEvidence(claimId).then(setEvidence).catch(() => setEvidence(null));
      // Guard the response against a slower request for an invoice already
      // closed or replaced, or a slow A landing on top of a fast B.
      listInvoicePhotos(claimId, full.id)
        .then((ps) => setViewPhotos((cur) => (viewingRef.current === full.id ? ps : cur)))
        .catch(() => setViewPhotos([]));
      document.body.classList.add("print-modal");
    } catch {
      setError("Unable to open invoice.");
    }
  }
  function closeView() {
    document.body.classList.remove("print-modal");
    viewingRef.current = null;
    setViewing(null);
    setViewPhotos([]);
  }
  async function patch(inv, data) {
    try {
      await updateInvoice(claimId, inv.id, data);
      await load();
    } catch (err) {
      setError(err.response?.data?.error || "Unable to update invoice.");
    }
  }
  function send(inv) {
    // The gate is enforced server-side; this only decides what to OFFER. A
    // refusal after the confirm dialog is two dead ends in a row — the useful
    // move is to take them to the thing that is missing (Robert, 14 Aug).
    if (evidence?.requires_photos && NEEDS_EVIDENCE.has(inv.invoice_type) && !inv.photo_count) {
      if (window.confirm(
        `Invoice #${inv.invoice_no} needs photographs before it can be sent.\n\nChoose them now?`
      )) setPicking(inv);
      return;
    }
    // A dialog rather than window.confirm, because sending is the moment the
    // FINAL question gets answered — see SendInvoiceModal.
    setSending({ inv, final: !!inv.is_final });
  }
  async function confirmSend() {
    const { inv, final } = sending;
    setBusy(true);
    try {
      // Final BEFORE sent, and in that order: is_final is a document field, so
      // the moment the invoice is sent it can only be changed by reopening.
      if (final !== !!inv.is_final) await updateInvoice(claimId, inv.id, { is_final: final });
      await markInvoiceSent(claimId, inv.id);
      setSending(null);
      await load();
    } catch (err) {
      // The send gate lives here: a missing photograph on an insurer that
      // requires them comes back as a sentence, not a silent refusal.
      setError(err.response?.data?.error || "Unable to mark the invoice as sent.");
      setSending(null);
    } finally {
      setBusy(false);
    }
  }
  async function reopen(inv) {
    try {
      await reopenInvoice(claimId, inv.id);
      await load();
    } catch (err) {
      setError(err.response?.data?.error || "Unable to reopen the invoice.");
    }
  }
  async function remove(inv) {
    if (!window.confirm(`Void invoice #${inv.invoice_no}? This releases whatever it billed (progress claim / quote lines / contractor costs) to be re-invoiced.`)) return;
    try {
      await voidInvoice(claimId, inv.id);
      await load();
    } catch (err) {
      setError(err.response?.data?.error || "Unable to void invoice.");
    }
  }

  useEffect(() => () => document.body.classList.remove("print-modal"), []);

  // THE SAVED PDF IS NAMED AFTER THE INVOICE (KS 2026-09-15). Chrome's Save as
  // PDF takes the file name from the page title, which is the claim
  // workspace's "#76562 Invoicing — ClaimsDesk 2.0" — so every invoice on a
  // job saved under the same name, and none under its own number. 1.0 named
  // it invoice number then GPS number, and the office files by that. Set
  // only while the document is open; the workspace's title comes back on
  // close. One place to change the shape, so keep it here.
  const viewingId = viewing?.id;
  const viewingNo = viewing?.invoice_no;
  const viewingClaimNo = viewing?.claim_number;
  useEffect(() => {
    if (!viewingId) return undefined;
    const previous = document.title;
    document.title = printFileName({ invoice_no: viewingNo, claim_number: viewingClaimNo });
    return () => {
      document.title = previous;
    };
  }, [viewingId, viewingNo, viewingClaimNo]);

  // THE SIDE PANEL'S SAVE (InvoiceSidePanel). The four fields are the JOB'S
  // (claims.invoice_to_1..3, invoice_quote_header; invoices.md §11), so they
  // go through updateClaim. The panel patches `viewing` as you type so the
  // document redraws live; leaving a box saves it. Errors show inside the
  // panel — the tab's own alert sits behind the fullscreen modal. The list
  // carries none of it.
  function patchViewing(patch) {
    setViewing((v) => (v ? { ...v, ...patch } : v));
  }
  async function saveInvoiceTo(fields) {
    await updateClaim(claimId, fields);
    patchViewing({ ...fields, ...invoiceToPreview(fields) });
  }

  return (
    <>
      <TabHeader
        title="Invoices"
        help="invoices"
        actions={
          <>
            {availableCycles.length > 0 ? (
              <>
                <ThemedSelect size="sm" style={{ width: 200 }} value={pickCycle} onChange={(e) => setPickCycle(e.target.value)} disabled={busy} data-hint="Create an invoice from a progress-claim cycle">
                  <option value="">From progress claim…</option>
                  {availableCycles.map((c) => (
                    <option key={c.id} value={c.id}>
                      Claim #{c.claim_no} (complete)
                    </option>
                  ))}
                </ThemedSelect>
                {pickCycle ? <Button size="sm" onClick={create} disabled={busy}>Create</Button> : null}
              </>
            ) : null}
            {creation?.can_create_quoted ? (
              <Button size="sm" variant="outline-primary" onClick={createQuoted} disabled={busy}
                data-hint={creation.job_kind === "quote" ? `Un-invoiced quote lines: ${money(creation.new_quote_lines_total)}` : undefined}>
                {creation.job_kind === "final_quote" ? "Create Final Quoted Invoice" : `Create Quoted Invoice (${money(creation.new_quote_lines_total)})`}
              </Button>
            ) : null}
            {creation?.unpaid_quoted_id ? (
              <Button size="sm" variant="outline-warning" onClick={replaceQuoted} disabled={busy}>Replace Quoted Invoice</Button>
            ) : null}
            {creation?.can_create_excess ? (
              <Button size="sm" variant="outline-primary" onClick={createExcess} disabled={busy}>Create Excess Invoice</Button>
            ) : null}
            <Form.Control size="sm" type="number" step="0.01" style={{ width: 130 }} value={otherAmount}
              placeholder="Other $ incl. GST" data-hint="Amount for an Other / Quote Fee invoice (GST inclusive)"
              onChange={(e) => setOtherAmount(e.target.value)} disabled={busy} />
            <Button size="sm" variant="outline-secondary" onClick={() => createOther("Other Invoice")} disabled={busy || otherAmount === ""}>
              Other
            </Button>
            <Button size="sm" variant="outline-secondary" onClick={() => createOther("Quote Fee Invoice")} disabled={busy}
              data-hint="Fixed $253 quote fee (or the amount entered)">
              Quote Fee
            </Button>
            {claim ? (
              <Form.Check
                type="switch"
                id="job-will-not-be-invoiced"
                className="text-nowrap ms-1"
                label="Will not be invoiced"
                checked={!!claim.job_will_not_be_invoiced}
                disabled={busy}
                data-hint="This job will never be invoiced — keeps it off the not-yet-invoiced worklists"
                onChange={(e) => toggleWillNotInvoice(e.target.checked)}
              />
            ) : null}
          </>
        }
      />

      {error ? <div className="alert alert-danger">{error}</div> : null}

      {/* Expected invoice dates (legacy InvoicesPage): the two-click generator
          (start month + % split, spread over month-ends) stays the fast path;
          Edit opens a per-row grid (custom dates / uneven splits — the
          medispace idea). Amounts = remaining uninvoiced × %. Feeds the RDR
          Accounts rule and the Predictive report (/rdr/predictive). */}
      <div className="border rounded px-3 py-2 mb-3">
        {expEdit === null ? (
          <div className="d-flex flex-wrap align-items-center column-gap-3 row-gap-2">
            <span className="text-uppercase fw-semibold small text-muted" style={{ letterSpacing: "0.06em" }}>
              Expected Invoice Dates
            </span>
            {expected.rows.length > 0 ? (
              <>
                <span className="small">
                  {expected.rows.map((x) => `${formatDate(x.expected_date)} (${money(x.expected_amount)})`).join(" · ")}
                </span>
                <Button size="sm" variant="outline-primary" disabled={busy} onClick={startExpectedEdit}>
                  Edit
                </Button>
                <Button size="sm" variant="outline-danger" disabled={busy}
                  onClick={() => run(() => clearExpectedInvoiceDates(claimId))}>
                  Clear
                </Button>
              </>
            ) : (
              <>
                <Form.Control size="sm" type="date" style={{ width: 155 }} value={expStart}
                  onChange={(e) => setExpStart(e.target.value)} disabled={busy} data-hint="First expected invoice date" />
                <ThemedSelect size="sm" style={{ width: 110 }} value={expPct} onChange={(e) => setExpPct(e.target.value)} disabled={busy}>
                  {["100", "50", "33.3", "25", "20", "10"].map((p) => (
                    <option key={p} value={p}>{p}%</option>
                  ))}
                </ThemedSelect>
                <Button size="sm" variant="outline-primary" disabled={busy || !expStart}
                  onClick={() => run(() => setExpectedInvoiceDates(claimId, expStart, expPct))}>
                  Set
                </Button>
                <Button size="sm" variant="outline-secondary" disabled={busy} onClick={startExpectedEdit}
                  data-hint="Enter dates and percentages by hand instead of an even split">
                  Custom…
                </Button>
                <span className="small text-muted">None set — the RDR chases active under-invoiced jobs without them.</span>
              </>
            )}
          </div>
        ) : (
          <>
            <div className="d-flex flex-wrap align-items-center column-gap-3 row-gap-2 mb-2">
              <span className="text-uppercase fw-semibold small text-muted" style={{ letterSpacing: "0.06em" }}>
                Expected Invoice Dates
              </span>
              <span className="small text-muted">
                Remaining to invoice (ex GST): <strong>{money(expected.remaining)}</strong> — each row expects remaining × %.
              </span>
              <Badge bg={expTotalPct === 100 ? "success" : "warning"} text={expTotalPct === 100 ? undefined : "dark"}
                data-hint="Percentages usually total 100 — under/over is allowed but worth a look">
                {expTotalPct}%
              </Badge>
            </div>
            {expEdit.map((row, i) => (
              <div key={i} className="d-flex flex-wrap align-items-center column-gap-2 row-gap-2 mb-2">
                <Form.Control size="sm" type="date" style={{ width: 155 }} value={row.expected_date}
                  onChange={(e) => patchExpectedRow(i, { expected_date: e.target.value })} disabled={busy} />
                <Form.Control size="sm" type="number" min="1" max="100" step="0.1" style={{ width: 90 }}
                  value={row.percentage} data-hint="Percentage of the remaining value"
                  onChange={(e) => patchExpectedRow(i, { percentage: e.target.value })} disabled={busy} />
                <span className="small text-muted" style={{ minWidth: 90 }}>
                  = {money(expected.remaining * (Number(row.percentage) || 0) / 100)}
                </span>
                <Button size="sm" variant="danger" disabled={busy || expEdit.length === 1}
                  onClick={() => setExpEdit(expEdit.filter((_, j) => j !== i))}>
                  Remove
                </Button>
              </div>
            ))}
            <div className="d-flex flex-wrap align-items-center column-gap-2">
              <Button size="sm" variant="success" disabled={busy}
                onClick={() => setExpEdit([...expEdit, { expected_date: "", percentage: "" }])}>
                + Add Date
              </Button>
              <Button size="sm" disabled={busy || expEdit.some((r) => !r.expected_date || !(Number(r.percentage) > 0))}
                onClick={saveExpectedRows}>
                Save
              </Button>
              <Button size="sm" variant="secondary" disabled={busy} onClick={() => setExpEdit(null)}>
                Cancel
              </Button>
            </div>
          </>
        )}
      </div>

      <Table striped bordered hover responsive size="sm" className="mb-0 align-middle">
        <thead className="table-surface">
          <tr>
            <th style={{ width: 90 }}>Invoice #</th>
            <th>Type</th>
            <th style={{ width: 110 }}>Date</th>
            <th className="text-end" style={{ width: 130 }}>Total</th>
            <th style={{ width: 150 }}>Status</th>
            {/* 1.0 shows this on the invoice list and 485 legacy invoices carry
                one — "CREATED IN ERROR" on 83656 being exactly why it matters.
                The text was imported all along; nothing displayed it. */}
            <th>Comments</th>
            <th className="text-end" style={{ width: 230 }}>Actions</th>
          </tr>
        </thead>
        <tbody>
          {invoices.map((inv) => (
            // A VOIDED invoice stays on the list but is plainly dead: greyed,
            // badged, and carrying no controls at all. Invoice numbers are issued
            // once and never reallocated, so hiding the row leaves an unexplained
            // gap in the numbering that reads as a lost invoice (Robert, 17 Aug).
            <tr key={inv.id} className={inv.voided ? "text-muted bg-body-tertiary" : undefined}>
              <td className="fw-semibold">{inv.invoice_no}</td>
              <td>
                {/* Progress-claim invoices say so (Karen 2026-07-15). */}
                {inv.source === "progress_claim" && inv.progress_claim_no
                  ? `Progress Claim ${inv.progress_claim_no}`
                  : inv.invoice_type}
                {inv.is_final ? <Badge bg="secondary" className="ms-1">Final</Badge> : null}
              </td>
              <td className="text-nowrap">
                {/* Editable (Karen 2026-07-15): pick the date the invoice is
                    dated — created as today, adjust here. A VOIDED invoice shows
                    it as text: nothing on a dead invoice is editable. SENT too
                    (2026-08-27): the server refuses invoice_date once sent, so a
                    live picker there was a dead control that could only 409 —
                    on every imported row, since the import marks history sent. */}
                {inv.voided || inv.sent_at ? (
                  formatDate(inv.invoice_date)
                ) : (
                  <Form.Control
                    size="sm"
                    type="date"
                    style={{ width: 140 }}
                    value={String(inv.invoice_date).slice(0, 10)}
                    onChange={(e) => e.target.value && patch(inv, { invoice_date: e.target.value })}
                    data-hint="Invoice date — click to change"
                  />
                )}
              </td>
              <td className="text-end text-nowrap">{money(inv.total)}</td>
              <td>
                {inv.voided ? (
                  <Badge bg="danger" className="me-1">VOIDED</Badge>
                ) : (
                  <>
                    {sentBadge(inv)}
                    {statusBadge(inv)}
                  </>
                )}
                {/* Legacy ddlExcessNotPaid: a canned reason resolves an unpaid
                    excess (waived / declined / charged to the insurer). */}
                {!inv.voided && inv.invoice_type === "Excess Invoice" && !inv.has_been_paid && notPaidReasons.length > 0 ? (
                  <ThemedSelect
                    size="sm"
                    className="mt-1"
                    value={inv.not_paid_reason || ""}
                    onChange={(e) => patch(inv, { not_paid_reason: e.target.value || null })}
                    disabled={busy}
                    data-hint="Why this excess won't be paid"
                  >
                    <option value="">Not paid — reason…</option>
                    {notPaidReasons.map((r) => (
                      <option key={r.id} value={r.name}>{r.name}</option>
                    ))}
                    {inv.not_paid_reason && !notPaidReasons.some((r) => r.name === inv.not_paid_reason) ? (
                      <option value={inv.not_paid_reason}>{inv.not_paid_reason}</option>
                    ) : null}
                  </ThemedSelect>
                ) : inv.not_paid_reason ? (
                  <div className="small text-muted">{inv.not_paid_reason}</div>
                ) : null}
              </td>
              <td className="small text-muted" style={{ whiteSpace: "pre-wrap" }}>{inv.comments || ""}</td>
              <td className="text-end text-nowrap">
                {/* A VOIDED invoice offers NOTHING. Not View either: getInvoice
                    404s on a soft-deleted row, so the button would open an error
                    — the same dead-control shape the "only actionable buttons
                    show" rule exists to prevent. The row still says what the
                    invoice was and that it was voided, which is the whole point
                    of keeping it. */}
                {inv.voided ? (
                  <span className="small">&mdash;</span>
                ) : (
                <>
                <Button variant="outline-primary" size="sm" className="me-1" onClick={() => view(inv)}>View</Button>
                {/* THE LEDGER DOES NOT BEGIN UNTIL THE INVOICE IS SENT. Paid and
                    disputed are facts about a document somebody has received; a
                    draft has been issued to nobody, so neither can be true of it
                    (Robert, 14 Aug: "can't be paid if it's open"). The service
                    refuses it too — this hides the button rather than holding a
                    separate opinion about the rule. */}
                {inv.sent_at ? (
                  <>
                    <Button variant={inv.has_been_paid ? "outline-secondary" : "outline-success"} size="sm" className="me-1"
                      onClick={() => patch(inv, { has_been_paid: !inv.has_been_paid })}>
                      {inv.has_been_paid ? "Unpay" : "Paid"}
                    </Button>
                    <Button variant={inv.disputed ? "outline-secondary" : "outline-warning"} size="sm" className="me-1"
                      onClick={() => patch(inv, { disputed: !inv.disputed })}>
                      {inv.disputed ? "Undispute" : "Dispute"}
                    </Button>
                  </>
                ) : null}
                {/* Mark/unmark as the job's FINAL invoice (Karen 2026-07-15:
                    "couldn't find where you mark invoice as final").

                    DRAFT ONLY, because is_final is DOCUMENT, not ledger: it
                    prints on the page (InvoiceView), so updateInvoice refuses it
                    once sent. This button was the one control left on every row,
                    and every one of the ~4,900 invoices carried over from 1.0 is
                    sent — so on a historical job it was a button that could only
                    ever produce an error. Set it before marking as sent, or
                    reopen to change it. */}
                {!inv.sent_at ? (
                  <Button variant={inv.is_final ? "outline-secondary" : "outline-dark"} size="sm" className="me-1"
                    data-hint="The job's final invoice — no more billing expected"
                    onClick={() => patch(inv, { is_final: !inv.is_final })}>
                    {inv.is_final ? "Unfinal" : "Final"}
                  </Button>
                ) : inv.is_final ? (
                  /* Still SAY so on a sent invoice — the flag drives three
                     Workflow Management rules, so hiding the button must not
                     hide the fact. */
                  <Badge bg="dark" className="me-1">Final</Badge>
                ) : null}
                {/* Photographs belong to THIS invoice, like inspection photos
                    belong to their inspection. Offered on every TYPE — the send
                    gate decides where they are required, not where they are
                    allowed — but NOT on an invoice that is already sent and has
                    none. That is every one of the ~4,900 invoices carried over
                    from 1.0: the button could only ever open a modal saying
                    "locked, reopen first", which is noise on almost every row of
                    almost every job (Robert, 14 Aug). A sent invoice that DOES
                    carry photographs keeps the button, so they can still be
                    looked at. */}
                {!inv.sent_at || inv.photo_count > 0 ? (
                  <Button variant="outline-secondary" size="sm" className="me-1" disabled={busy}
                    data-hint={inv.sent_at
                      ? "The photographs sent with this invoice"
                      : "Choose the finished photographs that go out with this invoice"}
                    onClick={() => setPicking(inv)}>
                    Photos{inv.photo_count > 0 ? ` (${inv.photo_count})` : ""}
                  </Button>
                ) : null}
                {/* ONLY ACTIONABLE BUTTONS SHOW (Robert, 14 Aug). A button that
                    can only produce an error message is worse than no button:
                    it invites the click, spends the user's attention, and
                    teaches them the screen is unreliable. Every one of these is
                    hidden when its own guard would refuse it, and the guards
                    live in services/invoices.js — this is the display half of
                    the same rules, not a second opinion about them.

                      Mark as Sent  — while the invoice is a draft
                      Reopen        — sent, and NOT paid (unmark paid first)
                      Void          — not sent, and not paid

                    Paid, Dispute and Final stay put: they are LEDGER, and the
                    ledger is editable for the life of the job. */}
                {!inv.sent_at ? (
                  <Button variant="outline-primary" size="sm" className="me-1" disabled={busy}
                    data-hint="Record that this invoice has gone out. Its lines, money, dates and photographs are then locked."
                    onClick={() => send(inv)}>
                    Mark as Sent
                  </Button>
                ) : !inv.has_been_paid ? (
                  <Button variant="outline-warning" size="sm" className="me-1" disabled={busy}
                    data-hint="Unlock this invoice so it can be corrected, then send it again. It keeps its number."
                    onClick={() => reopen(inv)}>
                    Reopen
                  </Button>
                ) : null}
                {!inv.sent_at && !inv.has_been_paid ? (
                  <Button variant="outline-danger" size="sm" disabled={busy} onClick={() => remove(inv)}>Void</Button>
                ) : null}
                </>
                )}
              </td>
            </tr>
          ))}
          {invoices.length === 0 ? (
            <tr><td colSpan={7} className="text-center text-muted">No invoices. Create one from a progress claim above.</td></tr>
          ) : null}
        </tbody>
      </Table>

      {picking ? (
        <InvoicePhotoPicker
          claimId={claimId}
          invoice={picking}
          // The most recent OTHER invoice on this claim, for copy-from-previous.
          previousInvoiceId={live.find((i) => i.id !== picking.id)?.id || null}
          onClose={() => setPicking(null)}
          onSaved={load}
        />
      ) : null}

      <SendInvoiceModal
        state={sending}
        busy={busy}
        // The list already carries is_final and sent_at on every row, so the
        // warning costs no request.
        otherFinal={sending
          ? live.filter((i) => i.is_final && i.id !== sending.inv.id)
          : []}
        onChange={(final) => setSending((s) => ({ ...s, final }))}
        onConfirm={confirmSend}
        onClose={() => setSending(null)}
      />

      {/* FULLSCREEN, THE PANEL ON THE LEFT, THE DOCUMENT ON THE RIGHT — the
          quote print's shape (Robert 2026-09-08, from medispace's Proposal
          tab), adopted here 2026-09-15 for KS's editable wording. The panel's
          column is no-print and the document column gives up its grid width
          on paper (print.css, invoice-print-layout). */}
      <Modal show={!!viewing} onHide={closeView} fullscreen>
        <Modal.Header closeButton className="no-print">
          <Modal.Title>Invoice #{viewing?.invoice_no} — GPS#{viewing?.claim_number}</Modal.Title>
        </Modal.Header>
        <Modal.Body className="bg-body-tertiary">
          <div className="row g-3 invoice-print-layout">
            <div className="col-lg-4 col-xl-3 no-print">
              <div className="sticky-top pt-1" style={{ top: 0 }}>
                {viewing ? (
                  <InvoiceSidePanel
                    invoice={viewing}
                    detailed={viewDetailed}
                    onDetailed={setViewDetailed}
                    onChange={patchViewing}
                    onSave={saveInvoiceTo}
                  />
                ) : null}
              </div>
            </div>
            <div className="col-lg-8 col-xl-9 invoice-print-preview">
              {/* PROGRESS CLAIMS ARE THE MASTER (Robert 2026-08-04): a Progress
                  Claim invoice row is a money snapshot of the progress claim behind
                  it, so this shows that same document — the one the Invoice button
                  on the Progress Claims tab produces — rather than a second,
                  thinner rendering of it. Two versions of one document is how they
                  drift apart. Falls back to the ordinary view if the link is
                  missing (16 of 276 legacy progress claims carry no invoice no). */}
              {viewing && viewing.progress_claim_id ? (
                <InvoiceDocument
                  claimId={claimId}
                  pcId={viewing.progress_claim_id}
                  invoiceTo={viewing.invoice_to}
                  header={viewing.invoice_quote_header}
                />
              ) : viewing ? (
                <InvoiceView invoice={viewing} detailed={viewDetailed} evidence={evidence} photos={viewPhotos} />
              ) : null}
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer className="no-print">
          {/* The closure-call warning (Robert 2026-08-17): this insurer's invoice
              shows the completion block, and the block's closure line comes ONLY
              from a Closure Call Client Signoff progress entry — so if none
              exists, the invoice prints without the client call the insurer is
              looking for. Said HERE, beside Print, because print is the moment
              the gap leaves the building. Scoped exactly as the photograph rule
              is (NEEDS_EVIDENCE): only the invoice types that claim work is
              done. Warn-only, never a block — plain Closure Call stays the
              operational flag and this must not stop an invoice going out. */}
          {viewing &&
          !viewing.progress_claim_id &&
          NEEDS_EVIDENCE.has(viewing.invoice_type) &&
          evidence?.shows_completion &&
          !(evidence.completion_notes || []).some((n) => n.closure_call_client_signoff) ? (
            <div className="w-100 alert alert-warning py-1 px-2 mb-2 small">
              No <strong>Closure Call Client Signoff</strong> progress entry on this job —
              this insurer&rsquo;s invoice shows completion, and it will print without the
              client&rsquo;s closure call. Record one on the Progress tab first.
            </div>
          ) : null}
          {/* And its PHOTOGRAPHS twin (KS 2026-08-28: "need both") — same
              scope as the send gate above (NEEDS_EVIDENCE + requires_photos +
              none chosen), said at the same moment for the same reason:
              print is when the gap leaves the building. Warn-only here; the
              send path already redirects to the picker. */}
          {viewing &&
          NEEDS_EVIDENCE.has(viewing.invoice_type) &&
          evidence?.requires_photos &&
          !viewing.photo_count ? (
            <div className="w-100 alert alert-warning py-1 px-2 mb-2 small">
              No <strong>photographs</strong> chosen for this invoice — this insurer requires
              them on the invoice, and sending will be refused until they are picked
              (the Photos button on the invoice row).
            </div>
          ) : null}
          {/* The All-details switch moved into the side panel (2026-09-15). */}
          <Button variant="outline-secondary" onClick={() => window.print()}>Print</Button>
          <Button variant="secondary" onClick={closeView}>Close</Button>
        </Modal.Footer>
      </Modal>
    </>
  );
}

export default ClaimInvoicesTab;
